export interface Alert {
  check_type: string;
  severity: string;
  message: string | null;
  created_at: string;
}

/** WL-1c: A single FAQ entry (Enterprise partners only). */
export interface FaqItem {
  question: string;
  answer: string;
}

export interface PortalBranding {
  logo_url: string | null;
  logo_dark_url: string | null;
  primary_color: string;
  display_name: string;
  support_email: string | null;
  support_phone: string | null;
  welcome_text: string | null;
  powered_by_visible: boolean;
  // WL-1c: Enterprise-only FAQ entries (null = not configured)
  faq_items: FaqItem[] | null;
  // AUDIT-SEC-2/S3: DSGVO compliance links
  privacy_url: string | null;
  impressum_url: string | null;
}

export interface PortalStatus {
  site_name: string;
  status: "ok" | "disruption";
  alerts: Alert[];
  branding: PortalBranding;
}

export async function fetchPortalStatus(token: string): Promise<PortalStatus> {
  const lang = navigator.language.startsWith("de") ? "de" : "en";
  const res = await fetch(`/api/v1/portal/${token}?lang=${lang}`);
  if (res.status === 404) throw new Error("not_found");
  if (!res.ok) throw new Error("server_error");
  return res.json();
}

export interface SpeedtestSession {
  correlation_token: string;
  correlation_supported: boolean;
}

export async function simulateSpeedtest(portalToken: string): Promise<SpeedtestResult> {
  const res = await fetch(`/api/v1/portal/${portalToken}/speedtest/simulate`, { method: "POST" });
  if (!res.ok) throw new Error("simulate_failed");
  const data = await res.json();
  return {
    correlation_status: data.correlation_status,
    download_mbps: data.download_mbps,
    upload_mbps: data.upload_mbps,
    ping_ms: data.ping_ms,
    jitter_ms: data.jitter_ms ?? null,
    speedtest_token: data.speedtest_token,
    matched_ip: data.matched_ip ?? null,
    matched_hostname: data.matched_hostname ?? null,
  };
}

export async function fetchSsids(portalToken: string): Promise<string[]> {
  const res = await fetch(`/api/v1/portal/${portalToken}/ssids`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.ssids ?? [];
}

export async function identifyByIp(
  portalToken: string,
  ip: string,
): Promise<{ matched: boolean; speedtest_token?: string; matched_hostname?: string | null }> {
  const res = await fetch(`/api/v1/portal/${portalToken}/identify-by-ip`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ip }),
  });
  if (!res.ok) throw new Error("identify_failed");
  return res.json();
}

// ---------------------------------------------------------------------------
// Speedtest measurement
// ---------------------------------------------------------------------------

export interface SpeedtestProgress {
  phase: "download" | "upload" | "ping";
  currentMbps: number;
  stageBytes: number;
  stageName: string;
}

export interface SpeedtestMeasurement {
  downloadMbps: number;
  uploadMbps: number;
  pingMs: number;
  jitterMs: number;
  totalBytesDownloaded: number;
}

const DOWNLOAD_STAGES: { bytes: number; name: string; timeoutMs: number }[] = [
  { bytes: 100 * 1024,        name: "100 KB", timeoutMs: 5_000  },
  { bytes: 1 * 1024 * 1024,  name: "1 MB",   timeoutMs: 8_000  },
  { bytes: 5 * 1024 * 1024,  name: "5 MB",   timeoutMs: 15_000 },
  { bytes: 10 * 1024 * 1024, name: "10 MB",  timeoutMs: 30_000 },
];

const UPLOAD_STAGES: { bytes: number; name: string }[] = [
  { bytes: 1 * 1024 * 1024, name: "1 MB" },
  { bytes: 5 * 1024 * 1024, name: "5 MB" },
];

const PING_COUNT = 10;
const UPLOAD_TIMEOUT_MS = 30_000;

function buildRandomBody(bytes: number): Uint8Array {
  // Zero-filled is sufficient for upload bandwidth measurement — no need for
  // crypto-random data, which is expensive on low-end mobile devices.
  return new Uint8Array(bytes);
}

function calcMedian(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function calcStdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance =
    values.reduce((s, v) => s + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

export async function runSpeedtest(
  portalToken: string,
  onProgress: (p: SpeedtestProgress) => void,
): Promise<SpeedtestMeasurement> {
  // -------------------------------------------------------------------------
  // Download
  // -------------------------------------------------------------------------
  let downloadWeightedSum = 0;
  let downloadTotalBytes = 0;
  let skipRemaining = false;

  for (const stage of DOWNLOAD_STAGES) {
    if (skipRemaining) break;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
      skipRemaining = true;
    }, stage.timeoutMs);

    try {
      const start = performance.now();
      const res = await fetch(`/api/v1/portal/${portalToken}/testfile?bytes=${stage.bytes}`, {
        signal: controller.signal,
      });
      if (!res.ok) {
        clearTimeout(timer);
        continue;
      }
      const buf = await res.arrayBuffer();
      clearTimeout(timer);

      const elapsed_s = (performance.now() - start) / 1000;
      const mbps = (buf.byteLength * 8) / elapsed_s / 1_000_000;

      downloadWeightedSum += buf.byteLength * mbps;
      downloadTotalBytes += buf.byteLength;

      onProgress({
        phase: "download",
        currentMbps: mbps,
        stageBytes: buf.byteLength,
        stageName: stage.name,
      });
    } catch {
      clearTimeout(timer);
      // Timeout or network error – skip remaining larger stages
      skipRemaining = true;
    }
  }

  const downloadMbps =
    downloadTotalBytes > 0 ? downloadWeightedSum / downloadTotalBytes : 0;

  // -------------------------------------------------------------------------
  // Upload
  // -------------------------------------------------------------------------
  let uploadWeightedSum = 0;
  let uploadTotalBytes = 0;

  for (const stage of UPLOAD_STAGES) {
    const body = buildRandomBody(stage.bytes);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

    try {
      const start = performance.now();
      const res = await fetch(`/api/v1/portal/${portalToken}/upload-test`, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/octet-stream" },
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (!res.ok) continue;

      const elapsed_s = (performance.now() - start) / 1000;
      const mbps = (stage.bytes * 8) / elapsed_s / 1_000_000;

      uploadWeightedSum += stage.bytes * mbps;
      uploadTotalBytes += stage.bytes;

      onProgress({
        phase: "upload",
        currentMbps: mbps,
        stageBytes: stage.bytes,
        stageName: stage.name,
      });
    } catch {
      clearTimeout(timer);
      // Network error or timeout – skip this stage, continue with next
    }
  }

  const uploadMbps =
    uploadTotalBytes > 0 ? uploadWeightedSum / uploadTotalBytes : 0;

  // -------------------------------------------------------------------------
  // Ping / Jitter
  // -------------------------------------------------------------------------
  const pingTimes: number[] = [];

  for (let i = 0; i < PING_COUNT; i++) {
    try {
      const start = performance.now();
      // AUDIT-PORTAL-1/L6: use dedicated /ping endpoint (no body overhead, no side-effects)
      const res = await fetch(`/api/v1/portal/${portalToken}/ping`);
      if (res.ok) {
        pingTimes.push(performance.now() - start);
      }
    } catch {
      // ignore individual ping failures
    }
  }

  const pingMs = pingTimes.length > 0 ? calcMedian(pingTimes) : 0;
  const jitterMs = pingTimes.length > 1 ? calcStdDev(pingTimes) : 0;

  onProgress({
    phase: "ping",
    currentMbps: 0,
    stageBytes: 0,
    stageName: "Ping",
  });

  return {
    downloadMbps,
    uploadMbps,
    pingMs,
    jitterMs,
    totalBytesDownloaded: downloadTotalBytes,
  };
}

export interface SpeedtestResult {
  correlation_status: "matched" | "no_match" | "error" | "pending";
  download_mbps: number | null;
  upload_mbps: number | null;
  ping_ms: number | null;
  jitter_ms: number | null;
  speedtest_token: string | null;
  matched_ip: string | null;
  matched_hostname: string | null;
}

export async function startSpeedtest(portalToken: string): Promise<SpeedtestSession> {
  const res = await fetch(`/api/v1/portal/${portalToken}/speedtest/start`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("start_failed");
  const data = await res.json();
  return {
    correlation_token: data.correlation_token,
    correlation_supported: data.correlation_supported ?? true,
  };
}

export async function completeSpeedtest(
  portalToken: string,
  correlationToken: string,
  results: {
    download_mbps?: number;
    upload_mbps?: number;
    ping_ms?: number;
    jitter_ms?: number;
    total_bytes_downloaded?: number;
  },
): Promise<SpeedtestResult> {
  const res = await fetch(
    `/api/v1/portal/${portalToken}/speedtest/${correlationToken}/complete`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(results),
    },
  );
  if (!res.ok) throw new Error("complete_failed");
  const data = await res.json();
  return {
    ...data,
    jitter_ms: data.jitter_ms ?? null,
    matched_ip: data.matched_ip ?? null,
    matched_hostname: data.matched_hostname ?? null,
  };
}

export interface CaseCreated {
  ticket_number: string;
  status: string;
  status_url: string;
}

export interface CaseStatus {
  ticket_number: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export async function submitCase(
  portalToken: string,
  submission: {
    device_type?: string;
    apartment?: string;
    since?: string;
    contact_email?: string;
    description: string;
    identification_method: string;
    speedtest_token?: string | null;
    matched_mac_hash?: string | null;
    language: string;
    hp_field?: string;
  },
): Promise<CaseCreated> {
  const res = await fetch(`/api/v1/portal/${portalToken}/cases`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(submission),
  });
  if (!res.ok) throw new Error("submit_failed");
  return res.json();
}

export async function fetchCaseStatus(
  portalToken: string,
  ticketNumber: string,
): Promise<CaseStatus> {
  const res = await fetch(`/api/v1/portal/${portalToken}/cases/${ticketNumber}`);
  if (res.status === 404) throw new Error("not_found");
  if (!res.ok) throw new Error("server_error");
  return res.json();
}

// ---------------------------------------------------------------------------
// Tenant case API (case_token based – CASE-1 Phase 4)
// ---------------------------------------------------------------------------

export interface TenantCaseEvent {
  id: string;
  event_type: string;
  actor: string;
  comment: string | null;
  created_at: string;
}

export interface TenantCaseResponse {
  ticket_number: string;
  status: string;
  priority: string;
  diagnosis_summary: string | null;
  contact_name: string | null;
  created_at: string;
  updated_at: string;
  events: TenantCaseEvent[];
}

export async function getTenantCase(token: string): Promise<TenantCaseResponse> {
  const res = await fetch(`/api/v1/tenant/cases/${token}`);
  if (res.status === 401 || res.status === 404) throw new Error("invalid_token");
  if (!res.ok) throw new Error("fetch_error");
  return res.json();
}

export async function postTenantComment(
  token: string,
  comment: string,
  firstName?: string,
  lastName?: string,
): Promise<void> {
  const body: Record<string, string> = { comment };
  if (firstName) body.first_name = firstName;
  if (lastName) body.last_name = lastName;
  const res = await fetch(`/api/v1/tenant/cases/${token}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { detail?: string }).detail || "comment_error");
  }
}

export async function postTenantSolved(token: string, comment: string): Promise<void> {
  const res = await fetch(`/api/v1/tenant/cases/${token}/solved`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  });
  if (!res.ok) throw new Error("solved_error");
}

export async function requestMagicLink(email: string): Promise<void> {
  const res = await fetch("/api/v1/tenant/request-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error("request_error");
}
